/* @flow */
import {
  DAY_RANGES,
  PUBLICATION_START_YEAR,
  TWENTY_FOUR_HOURS
} from '../constants';
import { compose } from 'redux';
import {map, every, isNil, range, isNull, omitBy, pickBy, inRange, isEmpty, memoize, isNumber, padStart, mapValues, takeWhile, rangeRight, partialRight} from 'lodash';
import format from 'date-fns/format';

type ArchivesState = {
  timestamp: ?number,
  year: ?number,
  month: ?number,
  day: ?number
};

const ARCHIVES_LANDING_PAGE_URL_FRAGMENT = 'landing-page';

const cachedDateInstance = new Date();
export const currentYear = cachedDateInstance.getFullYear();
export const currentMonth = cachedDateInstance.getMonth();
const currentDay = cachedDateInstance.getDate();

const isLeapYear =
  (year: number): bool =>
    year % 4 === 0;

const memoizedDaysInMonthFn = (
  selectedYear: number,
  selectedMonth: number,
): number =>
  selectedYear === currentYear && selectedMonth === currentMonth
    ? currentDay
    : selectedMonth === 1 && isLeapYear(selectedYear)
      ? DAY_RANGES[selectedMonth] + 1
      : DAY_RANGES[selectedMonth];

const daysInMonthResolver =
  (year: number, month: number): string =>
    [year, month].join(',');

const numDaysInMonth = memoize(
  memoizedDaysInMonthFn,
  daysInMonthResolver
);

export const publicationDayRange =
  ({ year, month }: ArchivesState): Array<number> =>
    every([year, month], isNumber)
      ? range(1, numDaysInMonth(year, month) + 1)
      : [];

export const publicationMonthRange = memoize(
  (selectedYear: number): Array<number> =>
    selectedYear === currentYear
      ? range(0, currentMonth + 1)
      : range(0, 12)
);

export const publicationYearRange = memoize(
  (editionId: number): Array<number> =>
    rangeRight(
      PUBLICATION_START_YEAR[editionId],
      currentYear + 1
    )
);

const padNumericDate = compose(
  partialRight(padStart, 2, '0'),
  String
);

const memoizedArchivesURLPathFn =
  (year: ?number, month: ?number, day: ?number): string => {
    const nullOrOneIndexedMonth = !isNil(month) && month + 1;
    const validDateFragments = map(
      takeWhile([year, nullOrOneIndexedMonth, day], isNumber),
      padNumericDate
    );
    const updatedURLFragment =
      isEmpty(validDateFragments)
        ? ARCHIVES_LANDING_PAGE_URL_FRAGMENT
        : validDateFragments.join('/');
    return `/archives/${updatedURLFragment}`;
  };

const archivesURLPathResolver =
  (year: ?number, month: ?number, day: ?number): string =>
    [year, month, day].join('-');

const memoizedArchivesURLPath = memoize(
  memoizedArchivesURLPathFn,
  archivesURLPathResolver
);

export const archivesURLPath =
  ({ year, month, day }: ArchivesState = {}): string =>
    memoizedArchivesURLPath(year, month, day);

type NumericArchivesState = { year: number, month: number, day: number };

const toTimestampOrNull =
  (timestamp: number): ?number =>
    isNaN(timestamp) ? null : timestamp;

const toSemanticDate =
  ({ year, month = 0, day = 1 }: NumericArchivesState): string =>
    `${month + 1}/${day}/${year}`;

const toTimestamp = compose(
  toTimestampOrNull,
  Date.parse,
  toSemanticDate,
  partialRight(omitBy, isNil)
);

const isWithinPublicationRange = (
  editionId: number,
  timestamp: number,
  currentTimestamp: number
): bool => inRange(
  timestamp,
  toTimestamp({ year: PUBLICATION_START_YEAR[editionId] }),
  currentTimestamp
);

/*
 * This utility only determines whether the year/month/day provided in the
 * archives state refers to a date preceding the current date.
 */
export const isValidArchivesDate = (
  editionId: number,
  { year, month, day }: ArchivesState = {},
  currentTimestamp: number = Date.now()
): bool =>
  isNumber(year)
  && (isNumber(month) || every([month, day], isNil))
  && isWithinPublicationRange(
    editionId,
    toTimestamp({ year, month, day }),
    currentTimestamp
  );

type NextAndPrevArchivesPaths = { next?: string, prev?: string };
type NextAndPrevTimestamps = { next?: number, prev?: number };
type EditionIdAndTimestampTuple = [number, NextAndPrevTimestamps];
type YearMonthDay = { year: number, month: number, day: number };

const dateToYearMonthDay =
  (date: Date): YearMonthDay => ({
    year: date.getFullYear(),
    month: date.getMonth(),
    day: date.getDate()
  });

const timestampToDate =
  (timestamp: number): Date =>
    new Date(timestamp);

export const timestampToYearMonthDay = compose(
  dateToYearMonthDay,
  timestampToDate
);

export const timestampToArchivesURLPath = compose(
  archivesURLPath,
  timestampToYearMonthDay
);

const filterValidArchivesDates = (
  [editionId, nextAndPrevTimestamps]: EditionIdAndTimestampTuple,
  currentTimestamp: number = Date.now()
): NextAndPrevArchivesPaths => pickBy(
  nextAndPrevTimestamps,
  (timestamp: number): bool => isWithinPublicationRange(
    editionId,
    timestamp,
    currentTimestamp
  )
);

const toNextAndPrevTimestamps =
  (editionId: number, timestamp: number): EditionIdAndTimestampTuple => [
    editionId,
    {
      next: timestamp + TWENTY_FOUR_HOURS,
      prev: timestamp - TWENTY_FOUR_HOURS
    }
  ];

const toFormattedArchivesPaths =
  (nextAndPrevTimestamps: NextAndPrevTimestamps): NextAndPrevArchivesPaths =>
    mapValues(
      nextAndPrevTimestamps,
      timestampToArchivesURLPath
    );

const nextAndPrevArchivesPathsResolver =
  (editionId: number, timestamp: number): string =>
    `${editionId}-${timestamp}`;

export const nextAndPrevArchivesPaths = memoize(
  compose(
    toFormattedArchivesPaths,
    filterValidArchivesDates,
    toNextAndPrevTimestamps
  ),
  nextAndPrevArchivesPathsResolver
);

export const getFirstOfMonthTimestamp =
  (month: number): number =>
    new Date().setMonth(month, 1);

type FormattedMessage =
  { id: string, description: string, defaultMessage: string };
type IntlObject =
  { formatMessage: Function, formatDate: Function };

const formatArchivesMessageFn = (
  message: string,
  { timestamp, month, day }: ArchivesState,
  intl: IntlObject
): string =>
  intl.formatMessage(message, {
    date: intl.formatDate(timestamp, omitBy({
      day: month && day ? 'numeric' : null,
      month: month ? 'long' : null,
      year: 'numeric'
    }, isNull))
  });

const formatArchivesMessageResolver = (
  { id }: FormattedMessage,
  { timestamp, year, month, day }: ArchivesState
): string =>
  [id, timestamp, year, month, day].join('-');

export const formatArchivesMessage = memoize(
  formatArchivesMessageFn,
  formatArchivesMessageResolver
);

const toPositiveNumberOrNull = compose(
  (num: number): ?number =>
    isNaN(num) || num <= 0
      ? null
      : num,
  Number
);

export const getArchivesState =
  (pathname: string): ArchivesState => {
    const dates = pathname.split('/').slice(3, 7);
    const [year, month, day] = map(dates, toPositiveNumberOrNull);
    const formattedDates = {
      year: year || null,
      month: every([year, month], isNumber) ? month - 1 : null,
      day: every([year, month, day], isNumber) ? day : null
    };
    return {
      ...formattedDates,
      timestamp: toTimestamp(formattedDates)
    };
  };

/*
 * Implicitly tested in ArchivesDropDownNavigation.
 *
 * This function only tests to see whether a change in the year or month in
 * the archives drop down navigation would result in impossible value for the
 * selected day. Use isValidArchivesDate() instead if you want to determine
 * whether an archives state object can be used to construct a valid date
 * preceding the current timestamp.
 *
 */
export const isValidDay =
  ({ year, month, day }: NumericArchivesState): bool => {
    if (year === null || month === null) {
      return false;
    }
    const [jan, feb, ...months] = DAY_RANGES;
    const dayRanges = isLeapYear(year)
      ? [jan, feb + 1, ...months]
      : DAY_RANGES;
    return day <= dayRanges[month]
      && !(year === currentYear && month === currentMonth && day > currentDay);
  };


export const dateInTheLastWeek = (date: string): bool => {
  const lastWeek = new Date();
  lastWeek.setDate(lastWeek.getDate() - 7);

  return Date.parse(date) > lastWeek;
};

export const formatInternationalDate = (
  locale: string,
  location: Object,
  windowOrGlobal: Object // Don't pass this in on prod; only for stubbing
  // `window`/`global` in unit testing.
): string => {
  if (!windowOrGlobal) {
    // This function works on client and server!
    windowOrGlobal = typeof window !== 'undefined' ? window : global;
  }
  const today = location.query.initialNow
    ? new Date(parseInt(location.query.initialNow))
    : new Date();

  let dateFormat;
  switch (locale) {
    case 'en-gb':
      dateFormat = 'dddd, D MMM, YYYY';
      break;
    case 'de-de':
      dateFormat = 'dddd, D. MMM, YYYY';
      break;
    case 'fr-fr':
      dateFormat = 'dddd D MMM YYYY';
      break;
    default:
      dateFormat = 'dddd MMM D, YYYY';
  }

  /*
   * Note: the `date-fns` locale is only present on the DE site. On US
   * and UK, it's `null` - we don't need any additional locale data
   * to render the English date string.
   */
  const dateFnsLocale = windowOrGlobal.__dateFnsLocales__[locale];
  return dateFnsLocale
    ? format(today, dateFormat, { locale: dateFnsLocale })
    : format(today, dateFormat);
};
